<template>
  <div class="fortune-wheel-form">
    <HeadSection
      :title="currentWheelId ? `Item ID ${currentWheelId}` : 'Create fortune wheel'"
      :breadcrumbs="breadcrumbs"
      permissionOfSection="fortune_wheel"
    >
      <template #actions v-if="!isOnlyView">
        <a-col :span="14" class="d-flex justify-content-end">
          <div class="d-flex">
            <a-button
              v-if="editPermission"
              type="primary"
              size="large"
              @click="save"
            >
              {{ currentWheelId ? 'Save' : 'Create' }}
            </a-button>
          </div>
        </a-col>
      </template>
    </HeadSection>
    <MainContainer :loading="userLoading" permissionOfSection="fortune_wheel">
      <div v-if="isOnlyView && currentWheel?.status === 'active'" class="d-flex mb-3" style="gap: 10px;">
        <div class="mt-1">
          <a-icon class="d-flex text-danger" type="info-circle" />
        </div>
        <span class="d-flex" style="max-width: 300px">
          To edit this wheel, you need to stop it first
        </span>
      </div>
      <a-form-model :model="form" :rules="rules" ref="form" class="d-flex flex-wrap" style="gap: 40px;">
        <div class="d-flex flex-column w-full" style="max-width: 500px;">
          <FormTitle title="General info" />
          <div class="d-flex flex-column" style="gap: 40px;">
            <a-row>
              <a-col>
                <Readonly
                  :show="isOnlyView"
                  title="Inner wheel name"
                  :value="form.name"
                  :required="true"
                  class="mb-4"
                >
                  <a-form-model-item
                    label="Inner wheel name"
                    prop="name"
                    :colon="false"
                  >
                    <a-input
                      v-model="form.name"
                      placeholder="Wheel name"
                      size="large"
                      :class="{ 'transparent-input transparent-input__view-mode' : isOnlyView }"
                      :disabled="loading || isOnlyView"
                    />
                  </a-form-model-item>
                </Readonly>
              </a-col>
              <div>
                <div class="d-flex align-items-center mb-2">
                  <span class="d-block mr-2 font-weight-bold font-size-14">Type</span>
                  <a-tooltip
                    placement="top"
                    arrow-point-at-center
                    :overlayStyle="{ 'white-space': 'normal', 'min-width': '240px' }"
                    :get-popup-container="getPopupContainer"
                  >
                    <template slot="title">
                      <span class="font-weight-light">Wheel type is set at creation and cannot be changed later</span>
                    </template>
                    <a-icon class="d-flex" type="info-circle" />
                  </a-tooltip>
                </div>
                <a-radio-group v-model="form.type" :disabled="isEditMode" class="mb-4">
                  <a-radio value="authorized">Authorized</a-radio>
                  <a-radio value="unauthorized">Unauthorized</a-radio>
                  <a-radio value="external">External</a-radio>
                </a-radio-group>
                <div v-if="isExternalType" class="d-flex">
                  <a-form-model-item
                    prop="external_key"
                    :rules="[{ required: isEditMode ? false : true, message: 'Token is required', trigger: ['change'] }]"
                    class="label-with-tooltip w-full"
                    :colon="false"
                  >
                    <template #label>
                      <div>
                        <span class="mr-2 mb-0 font-weight-bold" style="line-height: initial;">Token</span>
                        <a-tooltip
                          placement="top"
                          arrow-point-at-center
                          :overlayStyle="{ 'white-space': 'normal', 'min-width': '240px' }"
                          :get-popup-container="getPopupContainer"
                        >
                          <template slot="title">
                            <span class="font-weight-light">Token for identifying the wheel when requested from external platforms</span>
                          </template>
                          <a-icon class="d-flex" type="info-circle" />
                        </a-tooltip>
                      </div>
                    </template>
                    <div v-if="isEditMode && !form.external_key" class="token-input__skeleton">
                      <div class="noise secret">
                        <svg width="0" height="0">
                          <filter id="noiseFilter">
                            <feTurbulence
                              type="fractalNoise"
                              baseFrequency="0.7"
                              numOctaves="3"
                              stitchTiles="stitch"
                            />
                          </filter>
                        </svg>
                      </div>
                    </div>
                    <a-input
                      v-model="form.external_key"
                      style="width: 400px"
                      :placeholder="isEditMode ? '' : 'Create token'"
                      size="large"
                      class="token-input"
                      :class="{ 'transparent-input transparent-input__view-mode' : isOnlyView }"
                      :disabled="loading || isOnlyView"
                    >
                      <a-icon
                        v-if="form.external_key.length"
                        slot="addonBefore"
                        type="copy"
                        class="font-size-18"
                        :class="form.external_key.length ? 'text-primary cursor-pointer' : 'text-default cursor-default'"
                        @click="copy(form.external_key, 'Token copied to clipboard')"
                      />
                      <div
                        slot="addonAfter"
                        :class="{
                          'external-after-content' : !form.external_key && !isEditMode,
                          'external-after-content__change' : form.external_key || isEditMode,
                        }"
                        @click="toggleShowGenerateTokenModal(true)"
                      >
                        <a-button
                          v-if="!form.external_key && !isEditMode"
                          type="primary"
                          size="large"
                        >
                          Create
                        </a-button>
                        <a-icon
                          v-else
                          slot="addonBefore"
                          type="redo"
                          class="font-size-18 cursor-pointer text-default"
                        />
                      </div>
                    </a-input>
                  </a-form-model-item>
                </div>
              </div>
              <a-row class="d-flex align-items-center">
                <Readonly
                  :show="isOnlyView && !!form.starts_at"
                  title="Start date and time"
                  :value="form.starts_at"
                  :isDate="true"
                  style="max-width: 270px; align-self: flex-start;"
                >
                  <a-form-model-item label="Start date and time" prop="starts_at" :colon="false" style="max-width: 270px">
                    <a-date-picker
                      v-model="form.starts_at"
                      size="large"
                      format="YYYY-MM-DD HH:mm"
                      :disabled="loading || isOnlyView"
                      :disabled-date="disableDatesBeforeToday"
                      :disabled-time="disabledTimeBeforeToday"
                      :show-time="{
                        defaultValue: $moment('00:00:00', 'HH:mm'),
                        format: 'HH:mm'
                      }"
                      class="hover-date-picker"
                    />
                  </a-form-model-item>
                </Readonly>
                <span class="mx-3" :class="{'align-self-end mb-1' : isOnlyView && !!form.starts_at && !!form.ends_at}">—</span>
                <Readonly
                  :show="isOnlyView && !!form.ends_at"
                  title="End date and time"
                  :value="form.ends_at"
                  :isDate="true"
                  style="max-width: 270px; align-self: flex-start;"
                >
                  <a-form-model-item label="End date and time" prop="ends_at" :colon="false" style="max-width: 270px">
                    <a-date-picker
                      v-model="form.ends_at"
                      size="large"
                      format="YYYY-MM-DD HH:mm"
                      :disabled="loading || isOnlyView"
                      :disabled-date="disabledEndDate"
                      :disabled-time="disabledEndTime"
                      :show-time="{
                        defaultValue: $moment('00:00:00', 'HH:mm'),
                        format: 'HH:mm'
                      }"
                      class="hover-date-picker"
                    />
                  </a-form-model-item>
                </Readonly>
              </a-row>
              <a-row v-if="!isExternalType" class="w-full">
                <a-form-model-item prop="applications" required :colon="false" class="d-block">
                  <template #label>
                    <span class="mr-4">Apps</span>
                    <span v-if="applicationOptions.length && !isOnlyView" class="add-link cursor-pointer" @click.stop="toggleAppModal(true)">+ Add</span>
                  </template>
                  <div v-if="form.applications">
                    <a-tag
                      v-for="app in form.applications.slice(0, 5)"
                      :key="app.id"
                      style="
                        margin-right: 15px;
                        padding: 1px 8px;
                        border-radius: 2px;
                      "
                      class="cursor-pointer"
                      @click="toggleAppModal(true)"
                    >
                      {{ app.name }}
                      <a-icon v-if="app?.platform?.toLowerCase() === 'android'" type="android" class="ml-1 text-success" />
                      <a-icon v-if="app?.platform?.toLowerCase() === 'ios'" type="apple" class="ml-1 text-primary" />
                      <a-icon v-if="app?.platform?.toLowerCase() === 'web'" type="global" class="ml-1 text-default" />
                    </a-tag>
                    <a-tag v-if="form.applications.length > 4"> ... </a-tag>
                  </div>
                </a-form-model-item>
              </a-row>
              <a-row>
                <a-form-model-item prop="languages" required :colon="false" class="d-block">
                  <template #label>
                    <span class="mr-4">Languages</span>
                    <span v-if="!isOnlyView" class="add-link cursor-pointer" @click.stop="toggleShowEditLanguagesModal(true)">+ Add</span>
                  </template>
                  <div class="fortune-wheel-form__languages">
                    <div
                      v-for="(language, index) in form.languages"
                      :key="language.value"
                      class="fortune-wheel-form__language"
                      :class="{'cursor-pointer' : !isOnlyView}"
                    >
                      <div>
                        <span>{{ language.name }}</span>
                        <a-badge
                          v-if="language.is_default"
                          count="Default"
                          :number-style="{
                            backgroundColor: '#fff',
                            color: '#999',
                            boxShadow: '0 0 0 1px #d9d9d9 inset',
                          }"
                        />
                      </div>
                      <div v-if="!isOnlyView">
                        <span
                          v-if="!language.is_default"
                          class="fortune-wheel-form__language__set-default text-primary"
                          @click="changeDefaultLanguage(index)"
                        >
                          Set as default
                        </span>
                        <a-popconfirm
                          v-if="!language.is_default"
                          placement="left"
                          :disabled="loading"
                          title="Are you sure you want to delete this language?"
                          @confirm="() => deleteLanguage(language, index)"
                        >
                          <a-icon class="font-size-18 text-danger delete-icon" type="delete" />
                        </a-popconfirm>
                      </div>
                    </div>
                  </div>
                </a-form-model-item>
              </a-row>
            </a-row>
            <div v-if="!isExternalType" class="d-flex flex-column">
              <FormTitle title="Display conditions" />
              <div class="d-flex flex-column" style="gap: 20px;">
                <a-form-model-item
                  v-if="isAuthorizedType"
                  prop="segment_id"
                  class="label-with-tooltip w-full"
                  :colon="false"
                >
                  <template #label>
                    <div>
                      <span class="mr-2 mb-0 font-weight-bold" style="line-height: initial;">Select segment</span>
                      <a-tooltip
                        placement="top"
                        arrow-point-at-center
                        :overlayStyle="{ 'white-space': 'normal', 'min-width': '240px' }"
                        :get-popup-container="getPopupContainer"
                      >
                        <template slot="title">
                          <span class="font-weight-light">To create and edit segments, navigate to the <router-link to="/segments" class="text-primary" target="_blank">"Segments section"</router-link></span>
                        </template>
                        <a-icon class="d-flex" type="info-circle" />
                      </a-tooltip>
                    </div>
                  </template>
                  <UniversalSelect
                    v-model="form.segment_id"
                    class="w-full"
                    :options="segmentsOptions"
                    :showSearch="true"
                    :readonly="isOnlyView ? !!form.segment_id : false"
                    placeholder="Select segment"
                    :disabled="loading || isOnlyView"
                  />
                </a-form-model-item>
                <div class="d-flex justify-content-between" style="gap: 20px;">
                  <a-form-model-item prop="display_type" label="Display type" :colon="false" class="w-full mb-0">
                    <UniversalSelect
                      v-model="form.display_type"
                      :displayOptionsAtBottom="true"
                      :options="typeOptions"
                      :allowClear="false"
                      :readonly="isOnlyView ? !!form.display_type : false"
                      placeholder="Select type"
                      :disabled="loading || isOnlyView"
                      class="w-full"
                    />
                  </a-form-model-item>
                  <Readonly
                    :show="isOnlyView"
                    title="Display Frequency (days)"
                    :value="form.show_frequency"
                    :required="true"
                    class="w-full"
                  >
                    <a-form-model-item
                      prop="show_frequency"
                      label="Display Frequency (days)"
                      :colon="false"
                      class="d-block"
                    >
                      <a-input
                        v-model="form.show_frequency"
                        class="w-full"
                        placeholder="Display Frequency"
                        size="large"
                        :disabled="loading"
                        @keypress="(event) => {
                          if (!/[0-9]/.test(event.key)) {
                            event.preventDefault()
                          }
                        }"
                      />
                    </a-form-model-item>
                  </Readonly>
                </div>
              </div>
            </div>
            <div class="d-flex flex-column">
              <FormTitle title="Additional settings" />
              <a-form-model-item
                prop="info_link"
                :rules="[
                  { max: 255, message: 'Maximum 255 characters' }
                ]"
                :colon="false"
                class="label-with-tooltip"
              >
                <template #label>
                  <div>
                    <span class="mr-2 mb-0 font-weight-bold" style="line-height: initial;">Wheel info link</span>
                    <a-tooltip
                      placement="top"
                      arrow-point-at-center
                      :overlayStyle="{ 'white-space': 'normal', 'min-width': '350px' }"
                      :get-popup-container="getPopupContainer"
                    >
                      <template slot="title">
                        <span class="font-weight-light">Enter a link to the Wheel of Fortune page. If provided, a link icon will appear in the app. If not, the icon won’t be shown</span>
                      </template>
                      <a-icon class="d-flex" type="info-circle" />
                    </a-tooltip>
                  </div>
                </template>
                <a-input
                  v-model="form.info_link"
                  placeholder="Wheel info link"
                  allowClear
                  size="large"
                  class="w-full"
                  :class="{ 'transparent-input transparent-input__view-mode' : isOnlyView && form.info_link.length }"
                  :disabled="loading || isOnlyView"
                >
                  <a-icon
                    slot="addonBefore"
                    type="copy"
                    class="font-size-18"
                    :class="form.info_link.length ? 'text-primary cursor-pointer' : 'text-default cursor-default'"
                    @click="copy(form.info_link, 'Wheel info link copied to clipboard')"
                  />
                </a-input>
              </a-form-model-item>
              <div class="d-flex flex-column">
                <div v-if="isAuthorizedType" class="d-flex justify-content-between mb-2">
                  <div class="d-flex align-items-center">
                    <span class="d-block mr-2 font-size-14 font-weight-bold">Budget limit</span>
                    <a-tooltip
                      placement="top"
                      arrow-point-at-center
                      :overlayStyle="{ 'white-space': 'normal', 'min-width': '350px' }"
                      :get-popup-container="getPopupContainer"
                    >
                      <template slot="title">
                        <span class="font-weight-light" style="white-space: pre-wrap;">{{ 'When enabled, tracks the total denomination of freebet requests and shows the remaining balance. \n\nIt does not stop the wheel, only provides info.' }}</span>
                      </template>
                      <a-icon class="d-flex" type="info-circle" />
                    </a-tooltip>
                  </div>
                  <a-switch v-model="form.spin_budget_status" :disabled="isOnlyView" />
                </div>
                <div v-if="form.spin_budget_status && isAuthorizedType" class="d-flex justify-content-between" style="gap: 20px;">
                  <Readonly
                    :show="isOnlyView"
                    title="Limit amount"
                    :value="form.spin_budget"
                    :required="true"
                  >
                    <a-form-model-item
                      label="Limit amount"
                      prop="spin_budget"
                      :colon="false"
                      class="label-with-tooltip"
                    >
                      <a-input
                        v-model="form.spin_budget"
                        placeholder="Limit amount"
                        allowClear
                        size="large"
                        class="w-full"
                        :disabled="loading || isOnlyView"
                        @keypress="keypressNumbers($event)"
                      />
                    </a-form-model-item>
                  </Readonly>
                  <div class="d-flex align-items-end font-size-15 mb-4">
                    <p v-if="currentWheelId" class="mb-0">View budget stats in the <router-link :to="`/fortune-wheel/${currentWheelId}/statistics`" target="_blank" class="text-primary cursor-pointer underline">Statistics</router-link><br> after the wheel starts</p>
                    <p v-else class="mb-0">View budget stats in the Statistics<br> after the wheel starts</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="w-full empty-block" style="max-width: 100px; min-width: 0;" />
        <div class="d-flex flex-column w-full" style="max-width: 500px; min-width: 300px;">
          <FormTitle title="Content" />
          <div v-if="form.languages.length">
            <a-tabs
              v-model="activeLanguageId"
              :animated="{ inkBar: true, tabPane: false }"
              @change="changeActiveLanguageTab"
            >
              <a-tab-pane
                v-for="(language, languageIndex) in form.languages"
                :key="language.value"
                force-render
              >
                <template #tab>
                  <DefaultLabelTab
                    :name="language.name"
                    :error_validate="language.error_validate"
                    :is_default="language.is_default"
                  />
                </template>
                <div class="d-flex flex-column" style="gap: 20px;">
                  <a-row>
                    <Readonly
                      v-if="!isExternalType"
                      :show="isOnlyView"
                      title="Name"
                      :value="language.wheel_name"
                      :required="true"
                    >
                      <a-form-model-item label="Name" :prop="`languages.${languageIndex}.wheel_name`" :colon="false">
                        <a-input
                          v-model="language.wheel_name"
                          placeholder="Name"
                          size="large"
                          :disabled="loading || isOnlyView"
                          @focus="clearError(languageIndex)"
                        />
                      </a-form-model-item>
                    </Readonly>
                    <Readonly
                      v-if="!isExternalType"
                      :show="isOnlyView"
                      title="Description"
                      :value="language.wheel_description"
                      :required="true"
                    >
                      <a-form-model-item label="Description" :prop="`languages.${languageIndex}.wheel_description`" :colon="false">
                        <a-input
                          v-model="language.wheel_description"
                          placeholder="Description"
                          size="large"
                          :disabled="loading || isOnlyView"
                          @focus="clearError(languageIndex)"
                        />
                      </a-form-model-item>
                    </Readonly>
                    <Readonly
                      :show="isOnlyView"
                      title="Spin button name"
                      :value="language.button_name"
                      :required="true"
                    >
                      <a-form-model-item label="Spin button name" :prop="`languages.${languageIndex}.button_name`" :colon="false">
                        <a-input
                          v-model="language.button_name"
                          placeholder="Spin button name"
                          size="large"
                          :disabled="loading || isOnlyView"
                          @focus="clearError(languageIndex)"
                        />
                      </a-form-model-item>
                    </Readonly>
                  </a-row>
                </div>
              </a-tab-pane>
            </a-tabs>
          </div>
          <div v-else class="font-size-16">
            No language selected
          </div>
        </div>
      </a-form-model>
      <div v-if="deletePermission && currentWheelId && !isOnlyView" class="mt-5">
        <div
          class="d-flex align-items-center text-danger cursor-pointer hover-underline"
          style="width: fit-content;"
          @click="toggleShowDeleteModal(true)"
        >
          <a-icon type="delete" class="mr-2" />
          <span>Delete wheel</span>
        </div>
      </div>
    </MainContainer>
    <a-modal
      v-model="appsModal"
      title="Add new apps"
      :footer="null"
      width="700px"
      :destroyOnClose="true"
    >
      <AppsModal
        :modalAppsData="modalAppsData"
        :availableApplications="applicationOptions"
        :readonly="isOnlyView"
        @selectApps="changeSelectedApps"
        @close="toggleAppModal(false)"
      />
    </a-modal>
    <EditLanguagesModal
      v-if="showEditLanguagesModal"
      :show="showEditLanguagesModal"
      :allLanguages="languagesOptions"
      :languagesUsed="form.languages"
      @update="updateLanguages"
      @close="toggleShowEditLanguagesModal(false)"
    />
    <DefaultModal
      :show="showDeleteModal"
      title="Delete sector"
      text="Are you sure you want to delete this wheel?"
      description="All associated data will be lost"
      :actionButton="{
        text: 'Delete',
        type: 'danger',
      }"
      :loading="loading"
      @close="toggleShowDeleteModal"
      @action="deleteWheel"
    />
    <GenerateTokenModal
      v-if="showGenerateTokenModal"
      :show="showGenerateTokenModal"
      title="Change token"
      @createdToken="createdNewToken"
      @close="toggleShowGenerateTokenModal"
    />
  </div>
</template>

<script>
import apiClient from '@/services/axios'
import HeadSection from '@/components/main/headSection'
import MainContainer from '@/components/main/mainContainer'
import FormTitle from '@/components/custom/form/FormTitle.vue'
import DefaultLabelTab from '@/components/custom/form/DefaultLabelTab.vue'
import UniversalSelect from '@/components/custom/filters/universalSelect.vue'
import EditLanguagesModal from '@/components/custom/modals/EditLanguagesModal.vue'
import DefaultModal from '@/components/custom/modals/defaultModal'
import GenerateTokenModal from '@/views/apps/fortune-wheel/components/GenerateTokenModal.vue'
import Readonly from '@/components/custom/readonlyMode/readonly.vue'
import AppsModal from '@/components/custom/modals/appsModal.vue'
import { mapState } from 'vuex'
import { getErrorMessage } from '@/views/apps/fortune-wheel/composables/useGetErrorMessage.js'
import { copyToClipboard } from '@/composables/copy/copyToClipboard.js'
import { handleNumberKeyPress } from '@/views/apps/referral-program/composables/useHandleNumberKeyPress.js'
import { defaultLanguageName } from '@/views/apps/fortune-wheel/constants/defaultLanguageName.js'
import { displayTypeOptions } from '@/views/apps/fortune-wheel/constants/displayTypeOptions.js'
import { UTCFormatInCurrentTimezone, currentTimezoneFormatInUTC } from '@/views/apps/fortune-wheel/composables/useFormatDate.js'

const wheelApiUrl = process.env.VUE_APP_WHEEL_API_URL

export default {
  props: {
    createPermission: {
      type: Boolean,
      default: false,
    },
    editPermission: {
      type: Boolean,
      default: false,
    },
    deletePermission: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    HeadSection,
    MainContainer,
    FormTitle,
    DefaultLabelTab,
    UniversalSelect,
    AppsModal,
    DefaultModal,
    EditLanguagesModal,
    GenerateTokenModal,
    Readonly,
  },
  data: () => ({
    currentWheelId: null,
    currentWheel: null,
    segmentsOptions: [],
    applicationOptions: [],
    languagesOptions: [],
    languagesUsed: [],
    modalAppsData: null,
    activeLanguageId: undefined,
    form: {
      name: '',
      starts_at: undefined,
      ends_at: undefined,
      segment_id: undefined,
      applications: [],
      languages: [],
      show_frequency: undefined,
      display_type: undefined,
      info_link: '',
      type: 'authorized',
      external_key: '',
      spin_budget_status: false,
      spin_budget: undefined,
    },
    appsModal: false,
    showEditLanguagesModal: false,
    showDeleteModal: false,
    showGenerateTokenModal: false,
    selectedLanguage: null,
    loading: false,
  }),
  watch: {
    userLoading: {
      handler(val) {
        if (this.isOnlyView) {
          return
        }
        if (!val) {
          if (!this.editPermission && !this.createPermission) {
            this.$router.push(`/fortune-wheel/${this.$route?.params?.id}/`)
          }
        }
      },
      deep: true,
    },
  },
  computed: {
    ...mapState(['user']),
    userLoading() {
      return this.$store.state?.user?.loading || false
    },
    isOnlyView() {
      return this.$route.name === 'view-current-fortune-wheel'
    },
    isEditMode() {
      return !!this.currentWheelId
    },
    isAuthorizedType() {
      return this.form.type === 'authorized'
    },
    isExternalType() {
      return this.form.type === 'external'
    },
    breadcrumbs() {
      if (this.isEditMode) {
        return [
          { name: 'Fortune wheel', link: '/fortune-wheel' },
          { name: this.form.name, link: `/fortune-wheel/${this.currentWheelId}` },
          { name: this.isOnlyView ? 'View' : 'Edit', link: undefined },
        ]
      }
      return [
        { name: 'Fortune wheel', link: '/fortune-wheel' },
        { name: 'Create fortune wheel', link: undefined },
      ]
    },
    rules() {
      const currentRules = {
        name: [
          { required: true, message: 'Wheel name is required', trigger: ['blur', 'change'] },
          { max: 39, message: 'Maximum 39 characters' },
        ],
        starts_at: [{ required: true, message: 'Start date is required', trigger: ['blur', 'change'] }],
        ends_at: [{ required: true, message: 'End date is required', trigger: ['blur', 'change'] }],
        applications: [{ type: 'array', required: true, message: 'Please select at least one app', trigger: 'change' }],
        show_frequency: [{ required: true, message: 'Display frequency value is required', trigger: ['blur', 'change'] }],
        display_type: [{ required: true, message: 'Display type is required', trigger: ['blur', 'change'] }],
      }

      this.form.languages.forEach((_, index) => {
        currentRules[`languages.${index}.wheel_name`] = [
          { required: true, message: 'Name is required', trigger: ['blur', 'change'] },
        ]
        currentRules[`languages.${index}.wheel_description`] = [
          { required: true, message: 'Description is required', trigger: ['blur', 'change'] },
        ]
        currentRules[`languages.${index}.button_name`] = [
          { required: true, message: 'Button name is required', trigger: ['blur', 'change'] },
        ]
      })

      if (!this.isAuthorizedType) {
        return currentRules
      }

      if (!this.form.spin_budget_status) {
        return currentRules
      }

      currentRules.spin_budget = [{ required: true, message: 'Limit amount is required', trigger: ['blur', 'change'] }]
      return currentRules
    },
    typeOptions() {
      return displayTypeOptions
    },
  },
  methods: {
    getPopupContainer(trigger) {
      return trigger.parentElement
    },
    copy(value, message) {
      copyToClipboard(value, message)
    },
    keypressNumbers(e) {
      handleNumberKeyPress(e, true)
    },
    disableDatesBeforeToday(startValue) {
      const endValue = this.form.ends_at
      if (!startValue || !endValue) {
        return startValue && startValue < this.$moment().add(-1, 'day').endOf('day')
      }
      return startValue.valueOf() > endValue.valueOf() || startValue < this.$moment().add(-1, 'day').endOf('day')
    },
    disabledTimeBeforeToday(startValue) {
      if (!startValue) {
        return {}
      }
      const now = this.$moment()

      // если выбран сегодня — запретим все часы/минуты до текущего момента
      if (startValue.isSame(now, 'day')) {
        const disabledHours = []
        for (let i = 0; i < now.hour(); i++) {
          disabledHours.push(i)
        }
        const disabledMinutes = []
        if (startValue.hour() === now.hour()) {
          for (let i = 0; i < now.minute(); i++) {
            disabledMinutes.push(i)
          }
        }
        return {
          disabledHours: () => disabledHours,
          disabledMinutes: () => disabledMinutes,
        }
      }
      return {}
    },
    disabledEndDate(endValue) {
      const startValue = this.form.starts_at
      if (!endValue || !startValue) {
        return endValue && endValue < this.$moment().add(-1, 'day').endOf('day')
      }
      if (startValue.isSame(endValue, 'day')) {
        return false
      }
      return startValue.valueOf() >= endValue.valueOf()
    },
    disabledEndTime(endValue) {
      const startValue = this.form.starts_at
      if (!endValue || !startValue) {
        return {}
      }

      // если start и end один день → ограничиваем время
      if (startValue.isSame(endValue, 'day')) {
        const disabledHours = []
        for (let i = 0; i < startValue.hour(); i++) {
          disabledHours.push(i)
        }
        const disabledMinutes = []
        if (endValue.hour() === startValue.hour()) {
          for (let i = 0; i < startValue.minute(); i++) {
            disabledMinutes.push(i)
          }
        }
        return {
          disabledHours: () => disabledHours,
          disabledMinutes: () => disabledMinutes,
        }
      }
      return {}
    },
    createdNewToken(token) {
      this.form.external_key = token
    },
    changeActiveLanguageTab(langId) {
      const langIndex = this.form.languages.findIndex(lang => lang.value === langId)
      this.clearError(langIndex)
    },
    clearError(langIndex) {
      if (langIndex === -1) {
        return
      }
      this.form.languages[langIndex].error_validate = false
    },
    toggleAppModal(status) {
      if (this.loading) {
        return
      }
      this.appsModal = status
    },
    toggleShowEditLanguagesModal(status) {
      if (this.loading) {
        return
      }
      this.showEditLanguagesModal = status
    },
    toggleShowDeleteModal(status = false) {
      this.showDeleteModal = status
    },
    toggleShowGenerateTokenModal(status = false) {
      this.showGenerateTokenModal = status
    },
    updateLanguages(newLanguages) {
      const newLangIds = newLanguages.map(lang => lang.value)

      let updatedLanguages = []

      newLanguages.forEach(newLang => {
        if (!newLangIds.includes(newLang.value)) {
          return
        }

        const alreadyExists = this.form.languages.some(lang => lang.value === newLang.value)

        updatedLanguages.push({
          value: newLang.value,
          name: newLang.name,
          is_default: newLang.is_default,
          wheel_name: alreadyExists ? newLang.wheel_name : '',
          wheel_description: alreadyExists ? newLang.wheel_description : '',
          button_name: alreadyExists ? newLang.button_name : '',
          error_validate: alreadyExists ? newLang.error_validate : false,
        })
      })

      // перемещаем язык с is_default: true на первое место
      updatedLanguages = this.sortLanguages(updatedLanguages)

      this.form.languages = updatedLanguages
    },
    sortLanguages(languages) {
      return languages.sort((a, b) => (b.is_default ? 1 : 0) - (a.is_default ? 1 : 0))
    },
    changeDefaultLanguage(langIndex) {
      this.form.languages = this.form.languages.map(lang => {
        return { ...lang, is_default: false }
      })
      this.form.languages[langIndex].is_default = true
      this.form.languages = this.sortLanguages(this.form.languages)
    },
    editLanguage(lang) {
      this.selectedLanguage = lang
      this.toggleShowEditLanguagesModal(true)
    },
    deleteLanguage(lang, langIdx) {
      this.form.languages.splice(langIdx, 1)
      if (lang.value !== this.activeLanguageId) {
        return
      }
      this.activeLanguageId = this.form.languages[0].value
    },
    changeSelectedApps(apps) {
      this.toggleAppModal(false)
      this.form.applications = apps
      this.modalAppsData = apps
      this.validateApps()
    },
    async validateApps() {
      const isValid = await new Promise((resolve) => {
        this.$refs.form.validateField('applications', resolve)
      })
      return isValid
    },
    getFormData() {
      const data = {
        name: this.form.name,
        starts_at: this.form.starts_at ? currentTimezoneFormatInUTC(this.form.starts_at) : null,
        ends_at: this.form.ends_at ? currentTimezoneFormatInUTC(this.form.ends_at) : null,
        type: this.form.type,
        info_link: this.form.info_link,
      }
      data.languages = this.form.languages.map(lang => {
        const languageObj = {
          language_id: lang.value,
          button_name: lang.button_name,
          is_default: lang.is_default,
        }

        if (this.isExternalType) {
          return languageObj
        }

        languageObj.wheel_name = lang.wheel_name
        languageObj.wheel_description = lang.wheel_description
        return languageObj
      })
      if (this.currentWheel) {
        data.status = this.currentWheel.status
      }

      if (this.isExternalType) {
        if (this.form.external_key) {
          data.external_key = this.form.external_key
        }
        return data
      }

      data.show_frequency = this.form.show_frequency
      data.display_type = this.form.display_type
      data.applications = this.form.applications

      if (!this.isAuthorizedType) {
        return data
      }

      data.segment_id = this.form.segment_id
      data.spin_budget_status = this.form.spin_budget_status

      if (!this.form.spin_budget_status) {
        return data
      }

      data.spin_budget = this.form.spin_budget

      return data
    },
    checkingForContentFilling() {
      let isValid = true

      this.form.languages.forEach((_, index) => {
        if (
          !this.form.languages[index].wheel_name?.length ||
          !this.form.languages[index].wheel_description?.length ||
          !this.form.languages[index].button_name?.length
        ) {
          this.form.languages[index].error_validate = true
          isValid = false
        } else {
          this.form.languages[index].error_validate = false
        }
      })
      return isValid
    },
    async save() {
      // Делаем чтобы подсветить в каких табах ошибки
      this.checkingForContentFilling()

      this.$refs.form.validate(async (valid) => {
        if (valid) {
          const data = this.getFormData()
          try {
            this.loading = true
            let response
            if (this.isEditMode) {
              const url = `${wheelApiUrl}/admin/wheels/${this.currentWheelId}`
              response = await apiClient.patch(url, data)
            } else {
              const url = `${wheelApiUrl}/admin/wheels`
              response = await apiClient.post(url, data)
            }
            if (response?.data?.success) {
              const wheelId = response.data.data.id
              this.$router.push(`/fortune-wheel/${wheelId}`)
            } else {
              getErrorMessage(response?.data)
            }
          } finally {
            this.loading = false
          }
        } else {
          return false
        }
      })
      return false
    },
    setAvailableOptions({ applications, languages }) {
      this.applicationOptions = applications
      this.languagesOptions = languages.map(lang => {
        return { name: lang.name, value: lang.language_id }
      })
    },
    setDefaultValues() {
      const defaultLanguage = this.languagesOptions.find(lang => lang.name === defaultLanguageName) || this.languagesOptions[0]

      this.form.languages.push({
        name: defaultLanguage.name,
        value: defaultLanguage.id,
        is_default: true,
        wheel_name: '',
        wheel_description: '',
        button_name: '',
        error_validate: false,
      })

      this.activeLanguageId = defaultLanguage.id
    },
    setWheelData(data) {
      this.currentWheel = data
      this.form.name = data.name
      this.form.starts_at = data.starts_at ? UTCFormatInCurrentTimezone(data.starts_at, 'YYYY-MM-DD HH:mm', null) : undefined
      this.form.ends_at = data.ends_at ? UTCFormatInCurrentTimezone(data.ends_at, 'YYYY-MM-DD HH:mm', null) : undefined
      this.form.type = data.type
      this.form.segment_id = data.segment ? data.segment.id : undefined
      this.form.applications = data.applications
      this.modalAppsData = data.applications
      this.form.show_frequency = data.show_frequency
      this.form.display_type = data.display_type
      this.form.info_link = data?.info_link ? data.info_link : ''
      this.form.spin_budget_status = data?.spin_budget_status || false
      this.form.spin_budget = data?.spin_budget || undefined
      this.form.languages = data.languages.map(lang => {
        return {
          value: lang.language_id,
          name: lang.name,
          is_default: lang.is_default,
          wheel_name: lang.wheel_name,
          wheel_description: lang.wheel_description,
          button_name: lang.button_name,
          error_validate: false,
        }
      })
      this.activeLanguageId = this.form.languages[0].value
    },
    async deleteWheel() {
      try {
        this.loading = true

        const url = `${wheelApiUrl}/admin/wheels/${this.currentWheelId}`
        const response = await apiClient.delete(url)
        if (response?.data?.success) {
          this.$notification.success({
            message: 'Successfully',
            description: response?.data?.message || '',
          })
          this.$router.push('/fortune-wheel')
        } else {
          getErrorMessage(response?.data)
        }
      } catch (e) {
        this.$notification.error({
          message: e?.message ? e.message : 'Error',
        })
      } finally {
        this.loading = false
      }
    },
    async getWheelParams() {
      try {
        this.currentWheelId = this.$route.params.id
        const url = `${wheelApiUrl}/admin/wheels/${this.currentWheelId}`

        const response = await apiClient.get(url)
        if (response?.data?.success) {
          if (response.data.data.status === 'active' && !this.isOnlyView) {
            this.$router.push(`/fortune-wheel/${this.currentWheelId}`)
          }
          if (this.isOnlyView) {
            this.setAvailableOptions({ applications: response?.data?.data?.applications || [], languages: response?.data?.data?.languages || [] })
          }
          this.setWheelData(response.data.data)
        }
      } catch (e) {
        this.$notification.error({
          message: e?.message ? e.message : 'Error',
        })
      }
    },
    async getParams() {
      try {
        const url = `${wheelApiUrl}/admin/wheels/create`
        const response = await apiClient.get(url)
        if (response?.data?.success) {
          this.segmentsOptions = response.data.options.segments.map(segment => {
            return { name: segment.name, value: segment.id }
          })
          if (!this.isOnlyView) {
            this.applicationOptions = response?.data?.options?.applications || []
            this.languagesOptions = response.data.options.languages
          }
        }
      } catch (e) {
        this.$notification.error({
          message: e?.message ? e.message : 'Error',
        })
      }
    },
  },
  async mounted() {
    const requests = [this.getParams()]
    if (this.$route?.params?.id) {
      requests.push(this.getWheelParams())
    }
    try {
      this.loading = true
      await Promise.all(requests)
      if (this.isEditMode) {
        return
      }
      this.setDefaultValues()
    } finally {
      this.loading = false
    }
  },
}
</script>

<style lang="scss">
.fortune-wheel-form {
  &__languages {
    display: flex;
    flex-direction: column;
    gap: 5px;
  }
  &__language {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 38px;
    border: 1px solid #e4e9f0;
    border-radius: 4px;
    max-width: 400px;
    padding: 0 10px;
    &:hover {
      border-color: #40a9ff;
    }
    & > div {
      display: flex;
      align-items: center;
      gap: 10px;
    }
    &__set-default {
      &:hover {
        cursor: pointer;
        text-decoration: underline;
      }
    }
  }

  .token-input {
    & input {
      pointer-events: none;
    }
    &__skeleton {
      height: 24px;
      position: absolute;
      z-index: 1;
      bottom: 0;
      left: 20px;
      right: 60px;
      top: 50%;
      transform: translateY(-50%);
    }
  }

  .ant-radio-inner {
    border: 1px dashed #1890ff;
  }
  .ant-radio-checked .ant-radio-inner {
    border-style: solid;
  }

  .noise {
    background: rgba(43, 82, 120, 0.15);
    border-radius: 3px;
    filter: blur(5px);
    position: relative;
    overflow: hidden;
    height: 24px;
  }

  .noise.secret::before {
    content: "";
    position: absolute;
    inset: 0;
    background: rgba(255,255,255,0.15);
    filter: url(#noiseFilter);
    mix-blend-mode: screen;
    opacity: 0.6;
    animation: noiseShift 0.1s infinite linear;
    pointer-events: none;
    height: 24px;
  }

  @keyframes noiseShift {
    0%   { filter: url(#noiseFilter); }
    50%  { filter: url(#noiseFilter); }
    100% { filter: url(#noiseFilter); }
  }

  .external-after-content {
    margin: 0 -11px;
    &__change {
      padding: 8px 11px;
      margin: 0;
      & i {
        transition: all 0.4s ease;
        transform: rotate(90deg);
      }
    }
  }
  .ant-input-group-addon:has(.external-after-content__change) {
    padding: 0;
    &:hover {
      cursor: pointer;
      & i {
        transform: rotate(270deg);
        color: var(--kit-color-primary) !important;
      }
    }
  }

  .add-link {
    color: #007bff;
    font-weight: 400;
  }
  .hover-date-picker {
    & input {
      &:hover {
        cursor: pointer !important;
      }
    }
  }
}
@media (max-width: 1100px) {
  .fortune-wheel-form {
    .empty-block {
      display: none;
    }
  }
}

input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
input[type=number] {
  -moz-appearance:textfield;
}
</style>
